import React from 'react';
import { Link, useLocation } from 'react-router-dom';

const AppHeader = ({ demoTitle, apiDocUrl }) => {
  const location = useLocation();

  const handleHomeClick = () => {
    window.location.href = '/';
  };

  // Get appropriate icon based on demo route/type
  const getDemoIcon = () => {
    const path = location.pathname;
    
    if (path.includes('translator')) {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12.87 15.07l-2.54-2.51.03-.03A17.52 17.52 0 0014.07 6H17V4h-7V2H8v2H1v2h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7l1.62-4.33L19.12 17h-3.24z" fill="#6B7280"/>
        </svg>
      );
    } else if (path.includes('sentiment')) {
      return (
        <img 
          src="/assets/img/sentiment-analysis.png" 
          alt="Sentiment Analysis" 
          width="16" 
          height="16" 
          style={{ objectFit: 'contain' }}
        />
      );
    } else if (path.includes('text-to-speech')) {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" fill="#6B7280"/>
        </svg>
      );
    } else if (path.includes('speech-to-text')) {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z" fill="#6B7280"/>
        </svg>
      );
    }
    
    return null;
  };

  return (
    <header style={{ width: '100%', background: '#fff', borderBottom: '1px solid #eee', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', padding: '0 0 0 0', position: 'relative', zIndex: 100 }}>
      <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 64, padding: '0 24px' }}>
        {/* Left side - Breadcrumb navigation */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Smartly Logo */}
          <Link 
            to="/" 
            onClick={(e) => {
              e.preventDefault();
              handleHomeClick();
            }}
            style={{ 
              display: 'flex', 
              alignItems: 'center',
              textDecoration: 'none',
              cursor: 'pointer'
            }}
          >
            <img 
              src="/assets/img/smartlyai-logo.png" 
              alt="Smartly" 
              style={{ 
                height: '32px',
                width: 'auto'
              }} 
            />
          </Link>
          
          <span style={{ color: '#6B7280', fontSize: '16px' }}>/</span>
          
          {/* Models text */}
          <Link 
            to="/" 
            style={{ 
              color: '#6B7280', 
              fontSize: '16px', 
              fontWeight: '500',
              textDecoration: 'none',
              transition: 'color 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.color = '#374151'}
            onMouseLeave={(e) => e.target.style.color = '#6B7280'}
          >
            Models
          </Link>
          
          {/* Demo model info (only if on a demo page) */}
          {demoTitle && (
            <>
              <span style={{ color: '#6B7280', fontSize: '16px' }}>/</span>
              <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                {demoTitle}
              </span>
            </>
          )}
        </div>
        
        {/* Right side - API documentation link */}
        {apiDocUrl && (
          <div>
            <a 
              href={apiDocUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: '#FFFFFF',
                border: '1px solid #E2E8F0',
                borderRadius: '8px',
                padding: '8px 12px',
                textDecoration: 'none',
                color: '#374151',
                fontSize: '13px',
                fontWeight: '500',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#F9FAFB';
                e.target.style.borderColor = '#D1D5DB';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#FFFFFF';
                e.target.style.borderColor = '#E2E8F0';
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>Use this model via API</span>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 17L17 7M17 7H7M17 7V17" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
          </div>
        )}
      </div>
    </header>
  );
};

export default AppHeader;
